/* =============================================================================
   Puppygirl Paradise — the persistent temple
   A single WebGL scene that lives behind every page: coffered dome, colonnade,
   oculus shaft, drifting petals. Camera is driven by scroll position so the
   architecture moves with the reader rather than interrupting them.
   ========================================================================== */
import * as THREE from 'three';

const canvas = document.getElementById('pp-temple');
if (canvas && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  boot(canvas);
} else if (canvas) {
  canvas.remove();
}

function boot(canvas) {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 1.75));
  renderer.setSize(innerWidth, innerHeight);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.06;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(46, innerWidth / innerHeight, 0.4, 320);
  camera.position.set(0, 6.4, 66);

  /* ------------------------------------------------------------- palettes */
  const PAL = {
    light: {
      fog: 0xfceaf1,
      marble: 0xfffafc,
      marbleDark: 0xf6d5e2,
      gold: 0xd8b25c,
      shaft: 0xffffff,
      shaftPow: 0.36,
      key: 0xffffff,
      keyI: 2.7,
      amb: 0xfae0ea,
      ambI: 1.5,
      rim: 0xf9a8c4,
      rimI: 1.05,
      petal: 0xfdc0d4,
      exposure: 1.1,
    },
    dark: {
      fog: 0x120e18,
      marble: 0x2a2338,
      marbleDark: 0x171226,
      gold: 0xe7c978,
      shaft: 0xc9b9f5,
      shaftPow: 0.62,
      key: 0xd6c8ff,
      keyI: 2.1,
      amb: 0x2a2242,
      ambI: 0.9,
      rim: 0xff8fa8,
      rimI: 1.15,
      petal: 0xf0a8bc,
      exposure: 0.94,
    },
  };
  let mode = document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  const cur = {
    fog: new THREE.Color(PAL[mode].fog),
    marble: new THREE.Color(PAL[mode].marble),
    marbleDark: new THREE.Color(PAL[mode].marbleDark),
    gold: new THREE.Color(PAL[mode].gold),
    shaft: new THREE.Color(PAL[mode].shaft),
    key: new THREE.Color(PAL[mode].key),
    amb: new THREE.Color(PAL[mode].amb),
    rim: new THREE.Color(PAL[mode].rim),
    petal: new THREE.Color(PAL[mode].petal),
    keyI: PAL[mode].keyI,
    ambI: PAL[mode].ambI,
    rimI: PAL[mode].rimI,
    shaftPow: PAL[mode].shaftPow,
    exposure: PAL[mode].exposure,
  };
  scene.fog = new THREE.FogExp2(cur.fog.getHex(), 0.019);

  /* ------------------------------------------------------------- textures */
  const loader = new THREE.TextureLoader();
  loader.crossOrigin = 'anonymous';
  function tex(url, repeat) {
    const t = loader.load(url, () => (needsRender = true));
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    if (repeat) t.repeat.set(repeat[0], repeat[1]);
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
    return t;
  }
  // Texture URLs are handed in by the layout so the Shopify CDN paths stay in
  // Liquid rather than being hard coded in the script.
  const ENV = window.PP_ENV || {};
  const texMarble = tex(ENV.marble || 'pp-marble-cream.jpg', [5, 5]);
  const texCoffer = tex(ENV.coffer || 'pp-coffer-panel.jpg', [12, 5]);
  const texFlute = tex(ENV.flute || 'pp-fluting.jpg', [1, 3]);

  /* ---------------------------------------------------------------- lights */
  const amb = new THREE.HemisphereLight(cur.amb.getHex(), 0x1a1420, cur.ambI);
  scene.add(amb);
  const key = new THREE.SpotLight(cur.key.getHex(), cur.keyI, 190, 0.42, 0.65, 1.1);
  key.position.set(4, 62, 6);
  key.target.position.set(0, 0, 0);
  scene.add(key, key.target);
  const rim = new THREE.PointLight(cur.rim.getHex(), cur.rimI, 90, 2);
  rim.position.set(-16, 9, 12);
  scene.add(rim);
  const fill = new THREE.DirectionalLight(0xffffff, 0.28);
  fill.position.set(-8, 14, 22);
  scene.add(fill);

  /* ----------------------------------------------------------------- floor */
  const floorMat = new THREE.MeshStandardMaterial({
    map: texMarble,
    color: cur.marble,
    roughness: 0.28,
    metalness: 0.06,
  });
  const floor = new THREE.Mesh(new THREE.CircleGeometry(80, 96), floorMat);
  floor.rotation.x = -Math.PI / 2;
  scene.add(floor);

  // Inlaid gold ring set into the floor, centred beneath the oculus.
  const ringMat = new THREE.MeshStandardMaterial({
    color: cur.gold,
    roughness: 0.22,
    metalness: 0.92,
    emissive: cur.gold,
    emissiveIntensity: 0.1,
  });
  [
    [8.4, 8.7],
    [13.2, 13.42],
    [21.5, 21.72],
  ].forEach(([a, b]) => {
    const r = new THREE.Mesh(new THREE.RingGeometry(a, b, 128), ringMat);
    r.rotation.x = -Math.PI / 2;
    r.position.y = 0.012;
    scene.add(r);
  });

  /* --------------------------------------------------------------- columns */
  const colGeo = new THREE.CylinderGeometry(1.02, 1.16, 22, 26, 1, true);
  const colMat = new THREE.MeshStandardMaterial({
    map: texFlute,
    color: cur.marble,
    roughness: 0.42,
    metalness: 0.02,
    side: THREE.DoubleSide,
  });
  const COLS = 18;
  const columns = new THREE.InstancedMesh(colGeo, colMat, COLS * 2);
  const capGeo = new THREE.CylinderGeometry(1.5, 1.12, 1.5, 26);
  const capMat = new THREE.MeshStandardMaterial({
    color: cur.marble,
    roughness: 0.36,
    metalness: 0.04,
  });
  const caps = new THREE.InstancedMesh(capGeo, capMat, COLS * 2);
  const baseGeo = new THREE.CylinderGeometry(1.34, 1.5, 1.1, 26);
  const bases = new THREE.InstancedMesh(baseGeo, capMat, COLS * 2);
  const m4 = new THREE.Matrix4();
  let n = 0;
  for (let ring = 0; ring < 2; ring++) {
    const R = ring === 0 ? 24 : 33;
    for (let i = 0; i < COLS; i++) {
      const a = (i / COLS) * Math.PI * 2 + (ring ? Math.PI / COLS : 0);
      const x = Math.cos(a) * R;
      const z = Math.sin(a) * R;
      m4.makeTranslation(x, 12.1, z);
      columns.setMatrixAt(n, m4);
      m4.makeTranslation(x, 23.6, z);
      caps.setMatrixAt(n, m4);
      m4.makeTranslation(x, 0.55, z);
      bases.setMatrixAt(n, m4);
      n++;
    }
  }
  columns.instanceMatrix.needsUpdate = true;
  caps.instanceMatrix.needsUpdate = true;
  bases.instanceMatrix.needsUpdate = true;
  scene.add(columns, caps, bases);

  /* ------------------------------------------------------------ entablature */
  const entMat = new THREE.MeshStandardMaterial({
    color: cur.marbleDark,
    roughness: 0.5,
    metalness: 0.03,
    side: THREE.BackSide,
  });
  const ent = new THREE.Mesh(new THREE.CylinderGeometry(35, 35, 5.5, 96, 1, true), entMat);
  ent.position.y = 27;
  scene.add(ent);

  const frieze = new THREE.Mesh(
    new THREE.CylinderGeometry(24.6, 24.6, 3.4, 96, 1, true),
    new THREE.MeshStandardMaterial({
      map: texCoffer,
      color: cur.gold,
      roughness: 0.34,
      metalness: 0.6,
      side: THREE.BackSide,
    })
  );
  frieze.position.y = 26.1;
  scene.add(frieze);

  /* ------------------------------------------------------------------ dome */
  const domeGeo = new THREE.SphereGeometry(35, 96, 48, 0, Math.PI * 2, 0, Math.PI * 0.46);
  const domeMat = new THREE.MeshStandardMaterial({
    map: texCoffer,
    color: cur.marbleDark,
    roughness: 0.62,
    metalness: 0.05,
    side: THREE.BackSide,
  });
  const dome = new THREE.Mesh(domeGeo, domeMat);
  dome.position.y = 27.5;
  scene.add(dome);

  // Oculus rim
  const ocRim = new THREE.Mesh(
    new THREE.TorusGeometry(6.5, 0.7, 20, 96),
    new THREE.MeshStandardMaterial({
      color: cur.gold,
      roughness: 0.2,
      metalness: 0.95,
      emissive: cur.gold,
      emissiveIntensity: 0.28,
    })
  );
  ocRim.rotation.x = Math.PI / 2;
  ocRim.position.y = 58.4;
  scene.add(ocRim);

  /* ------------------------------------------------------------ light shaft */
  const shaftMat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    uniforms: {
      uColor: { value: cur.shaft.clone() },
      uPow: { value: cur.shaftPow },
      uTime: { value: 0 },
    },
    vertexShader: `
      varying vec2 vUv; varying vec3 vPos;
      void main(){ vUv=uv; vPos=position;
        gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }`,
    fragmentShader: `
      uniform vec3 uColor; uniform float uPow; uniform float uTime;
      varying vec2 vUv; varying vec3 vPos;
      void main(){
        float h = smoothstep(0.0, 0.55, vUv.y);          // fade toward the floor
        float edge = pow(sin(vUv.x*3.14159), 0.6);        // soften cylinder silhouette
        float shimmer = 0.86 + 0.14*sin(vUv.y*22.0 - uTime*0.7 + vUv.x*8.0);
        gl_FragColor = vec4(uColor, h*edge*uPow*shimmer);
      }`,
  });
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(7.4, 15.5, 58, 64, 1, true), shaftMat);
  shaft.position.y = 29;
  scene.add(shaft);

  // Warm pool where the shaft meets the floor
  const pool = new THREE.Mesh(
    new THREE.CircleGeometry(16, 72),
    new THREE.MeshBasicMaterial({
      color: cur.shaft,
      transparent: true,
      opacity: 0.2,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
  );
  pool.rotation.x = -Math.PI / 2;
  pool.position.y = 0.04;
  scene.add(pool);

  /* ---------------------------------------------------------------- petals */
  const PETALS = 460;
  const petalCanvas = document.createElement('canvas');
  petalCanvas.width = petalCanvas.height = 64;
  {
    const c = petalCanvas.getContext('2d');
    const g = c.createRadialGradient(32, 26, 2, 32, 32, 30);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.55, 'rgba(255,255,255,.85)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    c.fillStyle = g;
    c.beginPath();
    c.ellipse(32, 32, 15, 26, 0, 0, Math.PI * 2);
    c.fill();
  }
  const petalTex = new THREE.CanvasTexture(petalCanvas);
  const petalMat = new THREE.MeshBasicMaterial({
    map: petalTex,
    color: cur.petal,
    transparent: true,
    opacity: 0.75,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const petals = new THREE.InstancedMesh(new THREE.PlaneGeometry(0.42, 0.62), petalMat, PETALS);
  const pData = [];
  const dummy = new THREE.Object3D();
  for (let i = 0; i < PETALS; i++) {
    pData.push({
      r: 3 + Math.random() * 40,
      a: Math.random() * Math.PI * 2,
      y: Math.random() * 56,
      s: 0.5 + Math.random() * 1.1,
      vy: 0.55 + Math.random() * 1.5,
      va: (Math.random() - 0.5) * 0.16,
      sp: Math.random() * Math.PI * 2,
      tw: 0.6 + Math.random() * 2.2,
    });
  }
  scene.add(petals);

  /* ----------------------------------------------------------------- motes */
  const MOTES = 900;
  const mPos = new Float32Array(MOTES * 3);
  const mSeed = new Float32Array(MOTES);
  for (let i = 0; i < MOTES; i++) {
    const r = Math.sqrt(Math.random()) * 15;
    const a = Math.random() * Math.PI * 2;
    mPos[i * 3] = Math.cos(a) * r;
    mPos[i * 3 + 1] = Math.random() * 56;
    mPos[i * 3 + 2] = Math.sin(a) * r;
    mSeed[i] = Math.random() * 100;
  }
  const moteGeo = new THREE.BufferGeometry();
  moteGeo.setAttribute('position', new THREE.BufferAttribute(mPos, 3));
  moteGeo.setAttribute('aSeed', new THREE.BufferAttribute(mSeed, 1));
  const moteMat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: { uTime: { value: 0 }, uColor: { value: cur.shaft.clone() }, uSize: { value: 34 } },
    vertexShader: `
      attribute float aSeed; uniform float uTime; uniform float uSize;
      varying float vA;
      void main(){
        vec3 p = position;
        p.y = mod(p.y + uTime*0.42 + aSeed, 56.0);
        p.x += sin(uTime*0.28 + aSeed)*0.9;
        p.z += cos(uTime*0.22 + aSeed*1.3)*0.9;
        vec4 mv = modelViewMatrix * vec4(p,1.0);
        gl_PointSize = uSize / max(-mv.z, 1.0) * (0.5 + 0.5*sin(aSeed*3.1));
        vA = smoothstep(56.0, 8.0, p.y) * 0.75;
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `
      uniform vec3 uColor; varying float vA;
      void main(){
        float d = length(gl_PointCoord - 0.5);
        if (d > 0.5) discard;
        gl_FragColor = vec4(uColor, vA * (1.0 - d*2.0));
      }`,
  });
  const motes = new THREE.Points(moteGeo, moteMat);
  scene.add(motes);

  /* ------------------------------------------------------------ scroll rig */
  let scrollN = 0;
  let targetScroll = 0;
  function docHeight() {
    return Math.max(1, document.documentElement.scrollHeight - innerHeight);
  }
  addEventListener(
    'scroll',
    () => {
      targetScroll = window.scrollY / docHeight();
    },
    { passive: true }
  );

  let mx = 0,
    my = 0,
    tmx = 0,
    tmy = 0;
  addEventListener(
    'pointermove',
    (e) => {
      tmx = (e.clientX / innerWidth - 0.5) * 2;
      tmy = (e.clientY / innerHeight - 0.5) * 2;
    },
    { passive: true }
  );

  /* ------------------------------------------------------------ theme swap */
  window.addEventListener('pp:theme', (e) => {
    mode = e.detail.theme === 'dark' ? 'dark' : 'light';
  });

  function lerpCol(c, hex, t) {
    c.lerp(TMP.setHex(hex), t);
  }
  const TMP = new THREE.Color();

  /* ----------------------------------------------------------------- loop */
  let needsRender = true;
  const clock = new THREE.Clock();
  let paused = false;
  document.addEventListener('visibilitychange', () => {
    paused = document.hidden;
    if (!paused) clock.getDelta();
  });

  function frame() {
    requestAnimationFrame(frame);
    if (paused) return;
    const dt = Math.min(0.05, clock.getDelta());
    const t = clock.elapsedTime;

    // Ease colours toward the active palette instead of snapping.
    const P = PAL[mode];
    const k = 1 - Math.pow(0.001, dt);
    lerpCol(cur.fog, P.fog, k);
    lerpCol(cur.marble, P.marble, k);
    lerpCol(cur.marbleDark, P.marbleDark, k);
    lerpCol(cur.gold, P.gold, k);
    lerpCol(cur.shaft, P.shaft, k);
    lerpCol(cur.key, P.key, k);
    lerpCol(cur.amb, P.amb, k);
    lerpCol(cur.rim, P.rim, k);
    lerpCol(cur.petal, P.petal, k);
    cur.keyI += (P.keyI - cur.keyI) * k;
    cur.ambI += (P.ambI - cur.ambI) * k;
    cur.rimI += (P.rimI - cur.rimI) * k;
    cur.shaftPow += (P.shaftPow - cur.shaftPow) * k;
    cur.exposure += (P.exposure - cur.exposure) * k;

    scene.fog.color.copy(cur.fog);
    floorMat.color.copy(cur.marble);
    colMat.color.copy(cur.marble);
    capMat.color.copy(cur.marble);
    entMat.color.copy(cur.marbleDark);
    domeMat.color.copy(cur.marbleDark);
    ringMat.color.copy(cur.gold);
    ringMat.emissive.copy(cur.gold);
    frieze.material.color.copy(cur.gold);
    ocRim.material.color.copy(cur.gold);
    ocRim.material.emissive.copy(cur.gold);
    amb.color.copy(cur.amb);
    amb.intensity = cur.ambI;
    key.color.copy(cur.key);
    key.intensity = cur.keyI;
    rim.color.copy(cur.rim);
    rim.intensity = cur.rimI;
    shaftMat.uniforms.uColor.value.copy(cur.shaft);
    shaftMat.uniforms.uPow.value = cur.shaftPow;
    shaftMat.uniforms.uTime.value = t;
    moteMat.uniforms.uTime.value = t;
    moteMat.uniforms.uColor.value.copy(cur.shaft);
    petalMat.color.copy(cur.petal);
    pool.material.color.copy(cur.shaft);
    renderer.toneMappingExposure = cur.exposure;

    // Scroll: descend along the nave and slowly rise toward the oculus.
    scrollN += (targetScroll - scrollN) * Math.min(1, dt * 4.5);
    mx += (tmx - mx) * Math.min(1, dt * 3);
    my += (tmy - my) * Math.min(1, dt * 3);
    const s = scrollN;
    // A long walk from the entrance, through the colonnade, up to the oculus.
    camera.position.x = Math.sin(s * Math.PI * 1.35) * 6.5 + mx * 1.7;
    camera.position.z = 66 - s * 70;
    camera.position.y = 6.4 + s * 13 + my * -1.1;
    camera.lookAt(Math.sin(s * 2.6) * 3, 7 + s * 27, -6 - s * 10);
    camera.rotation.z = Math.sin(s * Math.PI * 2) * 0.017;

    // Petals fall, spin, and recycle at the floor.
    for (let i = 0; i < PETALS; i++) {
      const d = pData[i];
      d.y -= d.vy * dt * 2.1;
      d.a += d.va * dt;
      if (d.y < -1) {
        d.y = 56 + Math.random() * 8;
        d.r = 3 + Math.random() * 40;
      }
      dummy.position.set(
        Math.cos(d.a) * d.r + Math.sin(t * 0.5 + d.sp) * 0.8,
        d.y,
        Math.sin(d.a) * d.r + Math.cos(t * 0.42 + d.sp) * 0.8
      );
      dummy.rotation.set(t * d.tw * 0.5 + d.sp, t * d.tw * 0.35, Math.sin(t * 0.6 + d.sp) * 0.9);
      dummy.scale.setScalar(d.s);
      dummy.updateMatrix();
      petals.setMatrixAt(i, dummy.matrix);
    }
    petals.instanceMatrix.needsUpdate = true;

    renderer.render(scene, camera);
    if (!canvas.classList.contains('ready')) canvas.classList.add('ready');
  }
  frame();

  /* ---------------------------------------------------------------- resize */
  let rz;
  addEventListener('resize', () => {
    clearTimeout(rz);
    rz = setTimeout(() => {
      camera.aspect = innerWidth / innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(innerWidth, innerHeight);
      renderer.setPixelRatio(Math.min(devicePixelRatio, 1.75));
    }, 120);
  });

  window.PPTemple = { scene, camera, renderer, THREE };
}
